import { jSith } from "../jquery-replacement";
import { Proto } from "../../pojo/Proto";
import { S25Object } from "./s25.object";
import { isArray, isObject } from "./index";
import Result = Proto.Result;
import ResultObj = Proto.ResultObj;

export namespace S25Promise {
    export function all<T extends {}>(obj: T): Promise<{ [key in keyof T]: Awaited<T[key]> }> {
        if (isArray(obj)) {
            return Promise.all(obj as any) as any;
        } else if (isObject(obj)) {
            const arr: any[] = S25Object.propertyListToArray(obj);
            const arrP: any[] = arr.map((e: any) => e.value);
            return Promise.all(arrP).then((resp: any[]) => {
                const objP: any = {};
                for (let i = 0; i < resp.length; i++) {
                    objP[arr[i].prop] = resp[i];
                }
                return objP;
            });
        } else {
            return Promise.resolve(obj) as any;
        }
    }

    export function any(promiseArr: Promise<any>[]) {
        return Promise.allSettled(promiseArr).then((respArr) => {
            if (respArr?.length && respArr.every((resp) => resp.status === "rejected")) {
                return jSith.reject(respArr);
            } else {
                return jSith.resolve(respArr);
            }
        });
    }

    export function batch(
        batchFunc: (idx: number) => Promise<any>,
        batchSize: number,
        total: number,
        dynamicBatchSizeThresholdMs?: number,
        thresholdRangeMs?: number,
        hi?: number,
        lo?: number,
        currIdx?: number,
        promises?: Promise<any>[],
    ): Promise<any> {
        currIdx = currIdx || 0;
        promises = promises || [];
        batchSize = batchSize || 1;
        hi = hi || batchSize;
        lo = lo || batchSize;
        thresholdRangeMs = thresholdRangeMs || 500;

        for (let i = 0; i < batchSize; i++) {
            if (currIdx < total) {
                promises.push(batchFunc(currIdx));
                currIdx++;
            } else {
                return all(promises);
            }
        }

        const start = Date.now();
        return all(promises).then(() => {
            if (dynamicBatchSizeThresholdMs) {
                const elapsedMs = Date.now() - start;

                //we are under the threshold by a significant amount (the threshold range amount) -- we can increase batch size
                if (
                    elapsedMs < dynamicBatchSizeThresholdMs &&
                    dynamicBatchSizeThresholdMs - elapsedMs > thresholdRangeMs
                ) {
                    lo = batchSize; //low boundary is now the current batch size
                    hi = hi * 2; //double the high boundary to continue searching
                    batchSize = Math.ceil((hi + lo) / 2); //new batch size is the mid value (ceil to ramp up quicker)
                } else if (
                    elapsedMs > dynamicBatchSizeThresholdMs &&
                    elapsedMs - dynamicBatchSizeThresholdMs > thresholdRangeMs
                ) {
                    //we are over the threshold by a significant amount (the threshold range amount) -- we must decrease batch size

                    //high and low are the same, but we are still over the threshold. So low should go down bc the server, perhaps, has become overloaded
                    if (hi === lo) {
                        lo = Math.max(Math.floor(lo / 2), 1);
                    }

                    hi = batchSize; //high boundary is now the current batch size while low boundary remains the same (or was updated above)

                    batchSize = Math.max(Math.floor((hi + lo) / 2), 1); //new batch size is the mid value (floor to ramp down quicker)
                }
            }

            return batch(
                batchFunc,
                batchSize,
                total,
                dynamicBatchSizeThresholdMs,
                thresholdRangeMs,
                hi,
                lo,
                currIdx,
                promises,
            );
        });
    }

    export function asPromise<T>(maybePromise: T | Promise<T>): Promise<T> {
        if (maybePromise && maybePromise instanceof Promise) {
            return maybePromise;
        } else {
            return Promise.resolve(maybePromise);
        }
    }

    export function coalescePromise(maybePromise: any, then: Function) {
        if (maybePromise) {
            if (maybePromise.then) {
                return maybePromise.then(then);
            } else {
                // return Promise.resolve(then(maybePromise));
                return window.angBridge.$q.when(then(maybePromise));
            }
        } else {
            // return Promise.resolve();
            return window.angBridge.$q.when();
        }
    }

    // Returns a promise for a timeout
    export function delay(ms: number): Promise<void> {
        return new Promise((resolve) => jSith.timeout(() => resolve(), ms));
    }

    // A monad that takes a promise to resolve to the format [data, error]
    export function Maybe<T>(promise: Promise<T> | T, logErrors: boolean = true): Promise<Result<T, any>> {
        if (!promise) return Promise.resolve([promise as T, null]);
        if (typeof promise !== "object" || !("then" in promise)) return Promise.resolve([promise as T, null]);
        return promise.then(
            (data): [T, null] => [data, null],
            (error): [null, any] => {
                if (logErrors) console.error(error);
                return [null, error];
            },
        );
    }

    export async function Result<Data, Error = any>(
        promise: Promise<Data>,
        logErrors: boolean = true,
    ): Promise<ResultObj<Data, Error>> {
        const [data, error] = await Maybe(promise);
        return { data, error };
    }

    // Create a new promise and return it along with its resolvers
    export function createPromise<T>() {
        let resolve: (value?: T) => void;
        let reject: (reason?: any) => void;
        const promise = new Promise<T>((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    }

    /**
     * Returns a promise that resolves when the condition function returns true
     * @param condition
     * @param interval Milliseconds
     */
    export async function waitFor(condition: () => boolean, interval = 1_000): Promise<void> {
        while (!condition()) {
            await delay(interval);
        }
    }
}
